import { InfiniteData, useInfiniteQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import {
  getTicketActivity,
  GET_TICKET_ACTIVITY_QUERY_KEY,
  TGetTicketActivityResponse,
} from '../../api/get-list-ticket-activity.api';

interface IUseTicketActivityTabProps {
  ticketId: string;
  limit?: number;
}
const useTicketActivityTab = ({ ticketId, limit = 10 }: IUseTicketActivityTabProps) => {
  const { data, error, fetchNextPage, hasNextPage, isFetching, isFetchingNextPage, status } =
    useInfiniteQuery<TGetTicketActivityResponse>({
      queryKey: [
        GET_TICKET_ACTIVITY_QUERY_KEY,
        {
          ticketId,
          limit,
        },
      ],
      queryFn: ({ pageParam }): Promise<TGetTicketActivityResponse> =>
        getTicketActivity({
          ticketId,
          limit,
          cursor: pageParam ? String(pageParam) : undefined,
        }),
      initialPageParam: null as string | null,
      getNextPageParam: (lastPage) => {
        return lastPage?.payload?.pageInfo?.nextCursor ?? undefined;
      },
      enabled: !!ticketId,
      staleTime: 0,
      gcTime: 0,
    });

  // Transform infinite query data to flat array of activities
  const listTicketActivity = useMemo(() => {
    const pages = (data as InfiniteData<TGetTicketActivityResponse> | undefined)?.pages || [];
    const allLogs = pages.flatMap((page) => page?.payload?.spaceLogs || []);

    // Deduplicate by spaceLogId (in case of overlapping data)
    const uniqueLogs = Array.from(new Map(allLogs.map((log) => [log.spaceLogId, log])).values());

    return uniqueLogs;
  }, [data]);

  const isLoading = status === 'pending' || (isFetching && !data);
  const isError = status === 'error' || !!error;
  const isEmpty = listTicketActivity.length === 0 && !isLoading;

  return {
    listTicketActivity,
    hasNextPage,
    fetchNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
    isEmpty,
  };
};

export default useTicketActivityTab;
